import { Injectable, inject, NgZone } from '@angular/core';
import { BehaviorSubject, Subscription, firstValueFrom, interval } from 'rxjs';
import { take, takeWhile, tap } from 'rxjs/operators';
import { HomeRepositoryService } from './home-repository.service';
import { GameBalanceService } from 'src/app/services/game-balance.service';
import { AuthService } from '@core/auth.service';
import { TrackedEventsService } from 'src/app/services/tracked-events.service';
import { Data } from '@angular/router';
import { ChangeDetectorRef } from '@angular/core';
import { ClarityService } from 'src/app/services/clarity.service';

@Injectable({
  providedIn: 'root'
})
export class HomeCharityService {

  private homeRepo = inject(HomeRepositoryService);
  private gameBalanceService = inject(GameBalanceService);
  private authService = inject(AuthService);
  private trackedEventsService = inject(TrackedEventsService);
  private ngZone = inject(NgZone);
  private clarityService = inject(ClarityService);

  public spinnerSub: Subscription | null = null;

  voted$ = new BehaviorSubject(false);
  showVoteCard$ = new BehaviorSubject(false);

  trackCharityVoteEvents(isUpdatedCelebration: boolean): void {
    this.trackedEventsService?.listOfEvents$.pipe(take(1)).subscribe({
      next: (events) => {
        const voteEvent = events.find((event) => event?.id === "vote_to_charity");
        if (voteEvent) {
          this.trackedEventsService?.updateTrackedUserEvent("vote_to_charity");
          if (!isUpdatedCelebration) {
            this.trackedEventsService?.updateCelebration(voteEvent);
            isUpdatedCelebration = true;
          }
        }
      },
      error: () => { 
         // Intentionally left blank – error handled globally
      }
    });
  }

  async voteCharityAsync(
    charity: Data[],
    i: number,
    changeDetectorRef: ChangeDetectorRef,
    options?: { inlineSuccess?: boolean }
  ): Promise<boolean> {

    try {

      if (!charity) return false;

      const vote$ = this.gameBalanceService.voteCharity(
        charity[i]?.award_id,
        charity[i]?.charity_points
      );

      const data = await firstValueFrom(
        vote$.pipe(tap(() => changeDetectorRef.markForCheck()))
      );

      if (data?.status === "rejected") {

        const translatedMsg = await this.authService.getConstantsMsg(data?.error_code);
        const defaultMsg = {
          message: {
            line_1: "Something went wrong!",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };

        this.authService.showToastMessage(translatedMsg, defaultMsg);
        this.stopSpinner();
        charity[i].spinner.spinnerAnimation = false;
        return false;
      }

      if (data?.status === "FAIL") {

        const translatedMsg = await this.authService.getConstantsMsg("1164");
        const defaultMsg = {
          message: {
            line_1: "You do not have enough points to vote",
            line_2: "",
          },
          style: {
            icon: "tick circle error.svg",
            type: "Error",
          },
        };

        this.authService.showToastMessage(translatedMsg, defaultMsg);
        this.stopSpinner();
        charity[i].spinner.spinnerAnimation = false;
        return false;
      }

      if (data?.status === "success") {
        this.clarityService.event("vote_charity");

        charity[i].spinner.progressBar = 100;

        // Rehydrate shared member points balance after confirmed vote success
        // (LIVE adaptation of UAT hs-charity-vote applyVoteSuccess refresh).
        this.authService.refreshUserCurrentBalance();

        await this.homeRepo.updateCharityData(i, charity[i]?.reference);

        await new Promise<void>((resolve, reject) => {
          setTimeout(() => {
            try {
              this.finalizeVoteProcess(charity, i, changeDetectorRef, options);
              resolve();
            } catch (error) {
              reject(error);
            }
          }, 300);
        });

        return true;
      } else {
        this.stopSpinner();
        charity[i].spinner.spinnerAnimation = false;
        return false;
      }

    } catch {
       this.stopSpinner();
       if (charity?.[i]?.spinner) {
          charity[i].spinner.spinnerAnimation = false;
        }
      return false;
    }
  }

  private finalizeVoteProcess(
    charity,
    i,
    changeDetectorRef: ChangeDetectorRef,
    options?: { inlineSuccess?: boolean }
  ): void {
    this.stopSpinner();
    charity[i].spinner.spinnerAnimation = false;
    charity[i].spinner.proceed = false;

    this.ngZone.runOutsideAngular(() => {
      setTimeout(() => {
        this.ngZone.run(() => {
          charity[i].spinner.proceed = true;
          if (!options?.inlineSuccess) {
            this.voted$.next(true);
            this.showVoteCard$.next(true);
          }
          changeDetectorRef.markForCheck();
        });
      }, 500);
    });
  }

  startSpinner(
    charity,
    i: number,
    changeDetectorRef: ChangeDetectorRef
  ): void {

    if (!charity?.[i]?.spinner) {
      return;
    }
    this.stopSpinner();
    charity[i].spinner.spinnerAnimation = true;
    charity[i].spinner.progressBar = 0;

    const TARGET = charity[i].spinner.target ?? 100;

    const timer$ = interval(20);

    try {
      this.ngZone.runOutsideAngular(() => {
        this.spinnerSub = timer$
          .pipe(
            takeWhile((tick) => tick <= TARGET),
            tap((tick) => {
              charity[i].spinner.progressBar = tick;
              // Re-enter the Angular zone so OnPush views (home + home-scroll)
              // actually re-render progress at the same interval(20) speed.
              this.ngZone.run(() => changeDetectorRef.markForCheck());
            })
          )
          .subscribe({
            error: () => {
               // Intentionally left blank – error handled globally
             }
          });
      });
    } catch { 
      // Intentionally left blank – error handled globally
    }
  }

  public stopSpinner(): void {
    this.spinnerSub?.unsubscribe();
    this.spinnerSub = undefined;
  }

  ngOnDestroy(): void {
    this.spinnerSub?.unsubscribe();
  }

}